import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import VerifiedBadge from "../components/VerifiedBadge";
import {
  ShieldCheck, User as UserIcon, Building2, Upload, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Info, FileText, Camera,
} from "lucide-react";

/**
 * KycPage — Trust & Safety Center, user-facing.
 *
 * Two paths:
 *   • Individual: National ID front + back + selfie + structured fields
 *   • Business:   CR document + (optional) VAT cert + structured fields
 *
 * On submit, the record goes to admin review queue (manual verification).
 * No 3rd-party APIs, no costs.
 */
export default function KycPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("individual");
  const [meKyc, setMeKyc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(false);

  const reload = async () => {
    try {
      const { data } = await apiClient.get("/kyc/me");
      setMeKyc(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { if (user) reload(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/verify" }} replace />;

  const sub = meKyc?.submission;
  const status = meKyc?.kyc_status;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="kyc-page">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-[#8a6f2e]" />
        <div className="overline overline-gold">{lang === "ar" ? "مركز الثقة والأمان" : "Trust & Safety Center"}</div>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] font-medium mb-3">
        {lang === "ar" ? "التحقق من الهوية" : "Identity verification"}
      </h1>
      <p className="text-[#525252] leading-relaxed mb-6">
        {lang === "ar"
          ? "نطلب وثائقك مرة واحدة فقط لحماية المجتمع من الاحتيال. تظهر شارة \"موثّق\" بجانب اسمك وإعلاناتك بعد القبول."
          : "We collect your documents once to keep the marketplace safe. A \"Verified\" badge appears on your profile and listings once approved."}
      </p>

      {/* Current status banner */}
      {status && sub && (
        <div className="mb-6 surface rounded-2xl p-5" data-testid="kyc-status-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <VerifiedBadge status={status} type={sub.kyc_type} lang={lang} size="md" />
            <span className="text-sm text-[#525252]">
              {lang === "ar" ? "آخر تحديث:" : "Last update:"}{" "}
              {new Date(sub.reviewed_at || sub.submitted_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
            </span>
          </div>
          {sub.review_notes && (
            <div
              className={`mt-3 rounded-xl p-3 text-sm ${status === "rejected" ? "bg-red-50 text-red-800 border border-red-200" : "bg-[#fafaf7] text-[#3a3a3a] border border-[#c9a55c]/20"}`}
              data-testid="kyc-review-notes"
            >
              <strong className="block mb-1">{lang === "ar" ? "ملاحظات الفريق:" : "Team notes:"}</strong>
              {sub.review_notes}
            </div>
          )}
          {status === "approved" && (
            <p className="text-sm text-[#0a4f3a] mt-3 inline-flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> {lang === "ar" ? "حسابك موثّق. تظهر الشارة بجانب إعلاناتك." : "Your account is verified. The badge shows on your listings."}
            </p>
          )}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-2xl p-4 bg-[#0a4f3a]/5 border border-[#0a4f3a]/20 text-[#0a4f3a] text-sm inline-flex items-center gap-2" data-testid="kyc-success">
          <CheckCircle2 className="w-5 h-5" />
          {lang === "ar" ? "تم استلام طلبك. ستتم مراجعته خلال 24-48 ساعة." : "Submission received. Review takes 24-48 hours."}
        </div>
      )}

      {err && (
        <div className="mb-6 rounded-xl p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="kyc-error">
          {String(err)}
        </div>
      )}

      {/* Individual-only path — business verification is handled inside CARTAL Business → مساحاتي → نشاطي → استكمال التوثيق */}
      {status !== "approved" && status !== "pending" && (
        <>
          <div className="mb-6 rounded-2xl p-4 bg-[#fafaf7] border border-[#c9a55c]/25 flex items-start gap-3" data-testid="kyc-business-redirect-notice">
            <Building2 className="w-5 h-5 text-[#8a6f2e] mt-0.5 shrink-0" />
            <div className="text-sm text-[#3a3a3a] leading-relaxed">
              <div className="font-semibold mb-1">
                {lang === "ar" ? "لديك نشاط تجاري؟" : "Have a business?"}
              </div>
              <div className="text-[#525252]">
                {lang === "ar"
                  ? "توثيق الأنشطة التجارية (معرض / شركة / ورشة …) يتم من خلال "
                  : "Business verification (dealership / company / workshop …) is handled via "}
                <Link to="/businesses" className="text-[#8a6f2e] font-semibold underline decoration-[#c9a55c]/50 underline-offset-2 hover:decoration-[#c9a55c]" data-testid="kyc-goto-my-businesses">
                  {lang === "ar" ? "كارتال أعمال ← مساحاتي ← نشاطي ← استكمال التوثيق" : "CARTAL Business → My Spaces → My Activity → Complete Verification"}
                </Link>
                {lang === "ar" ? "." : "."}
              </div>
            </div>
          </div>

          <IndividualForm lang={lang} setBusy={setBusy} busy={busy} setErr={setErr} onDone={() => { setSuccess(true); reload(); }} />
        </>
      )}

      {/* Privacy note */}
      <div className="mt-8 flex items-start gap-2 rounded-xl p-3" style={{ backgroundColor: "#fafaf7", border: "1px solid rgba(201,165,92,0.30)" }}>
        <Info className="w-4 h-4 text-[#8a6f2e] mt-0.5 shrink-0" />
        <p className="text-xs text-[#525252] leading-relaxed">
          {lang === "ar"
            ? "وثائقك مشفّرة وتُحفظ بشكل آمن، ولا تُستخدم إلا للتحقق من الهوية. لن نشاركها مع أي طرف ثالث. تفاصيل في سياسة الخصوصية."
            : "Your documents are encrypted and securely stored, used solely for identity verification, and never shared. See our Privacy Policy."}
        </p>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition-colors ${active ? "border-[#c9a55c] text-[#0a0a0a] font-medium" : "border-transparent text-[#6b6b6b] hover:text-[#0a0a0a]"}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

// ─── File upload helper ─────────────────────────────────────────────────
function FileUpload({ label, value, onChange, lang, accept = "image/*,application/pdf", testid }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      setError(lang === "ar" ? "الحد الأقصى 8 ميجابايت" : "Max 8MB");
      return;
    }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await apiClient.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{label}</label>
      <div
        onClick={() => ref.current?.click()}
        className={`rounded-xl border-2 border-dashed p-4 cursor-pointer transition-colors ${value ? "border-[#0a4f3a]/40 bg-[#0a4f3a]/5" : "border-[#c9a55c]/40 hover:border-[#c9a55c] hover:bg-[#fafaf7]"}`}
        data-testid={testid}
      >
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-[#8a6f2e]"><Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "جارٍ الرفع..." : "Uploading..."}</div>
        ) : value ? (
          <div className="flex items-center justify-center gap-2 text-[#0a4f3a] text-sm">
            <CheckCircle2 className="w-4 h-4" /> {lang === "ar" ? "تم الرفع بنجاح — اضغط لتغيير" : "Uploaded — click to replace"}
          </div>
        ) : (
          <div className="text-center text-[#6b6b6b] text-sm">
            <Upload className="w-5 h-5 mx-auto mb-1 text-[#8a6f2e]" />
            {lang === "ar" ? "اضغط للرفع (صورة أو PDF — حد أقصى 8 ميجا)" : "Click to upload (image or PDF, max 8MB)"}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
    </div>
  );
}

// ─── Individual KYC form ────────────────────────────────────────────────
function IndividualForm({ lang, busy, setBusy, setErr, onDone }) {
  const [f, setF] = useState({
    full_name_ar: "", national_id: "", nationality: "saudi", dob: "",
    id_front_url: "", id_back_url: "", selfie_url: "",
  });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await apiClient.post("/kyc/individual", f);
      onDone();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const ready = f.full_name_ar && f.national_id && f.dob &&
                f.id_front_url && f.id_back_url && f.selfie_url;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="kyc-form-individual">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={lang === "ar" ? "الاسم الكامل (بالعربية)" : "Full name (Arabic)"}>
          <input className="input-dark" required minLength={4} value={f.full_name_ar} onChange={(e) => upd("full_name_ar", e.target.value)} data-testid="kyc-name" />
        </Field>
        <Field label={lang === "ar" ? "رقم الهوية الوطنية / الإقامة" : "National ID / Iqama"}>
          <input className="input-dark" required pattern="[12][0-9]{9}" inputMode="numeric"
            placeholder="1xxxxxxxxx / 2xxxxxxxxx"
            value={f.national_id} onChange={(e) => upd("national_id", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="kyc-national-id" />
        </Field>
        <Field label={lang === "ar" ? "الجنسية" : "Nationality"}>
          <select className="input-dark" value={f.nationality} onChange={(e) => upd("nationality", e.target.value)} data-testid="kyc-nationality">
            <option value="saudi">{lang === "ar" ? "سعودي" : "Saudi"}</option>
            <option value="resident">{lang === "ar" ? "مقيم" : "Resident"}</option>
            <option value="gcc">{lang === "ar" ? "خليجي" : "GCC"}</option>
            <option value="other">{lang === "ar" ? "أخرى" : "Other"}</option>
          </select>
        </Field>
        <Field label={lang === "ar" ? "تاريخ الميلاد" : "Date of birth"}>
          <input className="input-dark" type="date" required value={f.dob} onChange={(e) => upd("dob", e.target.value)} data-testid="kyc-dob" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <FileUpload label={lang === "ar" ? "صورة الهوية (الوجه الأمامي)" : "ID — front"}
          value={f.id_front_url} onChange={(v) => upd("id_front_url", v)} lang={lang} testid="kyc-id-front" />
        <FileUpload label={lang === "ar" ? "صورة الهوية (الوجه الخلفي)" : "ID — back"}
          value={f.id_back_url} onChange={(v) => upd("id_back_url", v)} lang={lang} testid="kyc-id-back" />
        <FileUpload label={lang === "ar" ? "صورة شخصية (سيلفي)" : "Selfie"}
          value={f.selfie_url} onChange={(v) => upd("selfie_url", v)} lang={lang} testid="kyc-selfie" />
      </div>

      <button type="submit" disabled={!ready || busy} className="btn-primary" data-testid="kyc-submit-individual">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        {lang === "ar" ? "إرسال للتحقق" : "Submit for verification"}
      </button>
    </form>
  );
}

// ─── Business KYC form ──────────────────────────────────────────────────
function BusinessForm({ lang, busy, setBusy, setErr, onDone }) {
  const [f, setF] = useState({
    business_name_ar: "", business_name_en: "", cr_number: "", vat_number: "",
    owner_national_id: "", address: "", cr_document_url: "", vat_document_url: "",
  });
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await apiClient.post("/kyc/business", { ...f, vat_document_url: f.vat_document_url || null, vat_number: f.vat_number || null, business_name_en: f.business_name_en || null });
      onDone();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const ready = f.business_name_ar && f.cr_number && f.owner_national_id && f.address && f.cr_document_url;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="kyc-form-business">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={lang === "ar" ? "اسم النشاط (عربي)" : "Business name (Arabic)"}>
          <input className="input-dark" required value={f.business_name_ar} onChange={(e) => upd("business_name_ar", e.target.value)} data-testid="kyc-biz-name-ar" />
        </Field>
        <Field label={lang === "ar" ? "اسم النشاط (إنجليزي)" : "Business name (English)"}>
          <input className="input-dark" value={f.business_name_en} onChange={(e) => upd("business_name_en", e.target.value)} data-testid="kyc-biz-name-en" />
        </Field>
        <Field label={lang === "ar" ? "رقم السجل التجاري" : "Commercial Registration #"}>
          <input className="input-dark" required pattern="[0-9]{10}" inputMode="numeric" placeholder="10 digits"
            value={f.cr_number} onChange={(e) => upd("cr_number", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="kyc-cr-number" />
        </Field>
        <Field label={lang === "ar" ? "الرقم الضريبي (اختياري)" : "VAT number (optional)"}>
          <input className="input-dark" inputMode="numeric" value={f.vat_number} onChange={(e) => upd("vat_number", e.target.value.replace(/\D/g, "").slice(0, 15))} data-testid="kyc-vat" />
        </Field>
        <Field label={lang === "ar" ? "هوية المالك" : "Owner National ID"}>
          <input className="input-dark" required pattern="[12][0-9]{9}" inputMode="numeric"
            value={f.owner_national_id} onChange={(e) => upd("owner_national_id", e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="kyc-owner-id" />
        </Field>
        <Field label={lang === "ar" ? "العنوان الكامل" : "Full address"}>
          <input className="input-dark" required value={f.address} onChange={(e) => upd("address", e.target.value)} data-testid="kyc-address" />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <FileUpload label={lang === "ar" ? "وثيقة السجل التجاري" : "CR document"}
          value={f.cr_document_url} onChange={(v) => upd("cr_document_url", v)} lang={lang} testid="kyc-cr-doc" />
        <FileUpload label={lang === "ar" ? "شهادة ضريبية (اختياري)" : "VAT certificate (optional)"}
          value={f.vat_document_url} onChange={(v) => upd("vat_document_url", v)} lang={lang} testid="kyc-vat-doc" />
      </div>

      <button type="submit" disabled={!ready || busy} className="btn-primary" data-testid="kyc-submit-business">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        {lang === "ar" ? "إرسال للتحقق" : "Submit for verification"}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#3a3a3a] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
